///
/// EarthBrowser
///
/// Created by Matt Giger on 4/13/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import {
	CoordFooter,
	CurrentConditionsSection,
	InfoField,
	MoreSection
} from "@/components/controls/anchored-panel/panelAtoms"
import { Constants } from "@/constants"
import { CountriesTable } from "@/earthbrowser/dataset-distribution/locations/CountriesTable"
import { formatAltitude } from "@/earthbrowser/utils/unitFormatters"
import type { VolcanoInfo } from "@shared-types/volcano-info"
import { useEffect, useState } from "react"

export function AnchoredVolcanoInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "volcano" ? data : null}
			fallbackTitle={content => content.volcano.name}
			accentBorder="border-orange-400/20"
			pill="bg-orange-400/90"
		>
			{(content, ctx) => <VolcanoBody volcano={content.volcano} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

function VolcanoBody({ volcano, ctx }: { volcano: VolcanoInfo; ctx: PanelShellContext }) {
	const [countryCode, setCountryCode] = useState<string | null>(null)
	useEffect(() => {
		if (!volcano.country) return
		CountriesTable.select(volcano.country).then(results => {
			if (results.length > 0) {
				setCountryCode(results[0].country_code.toLowerCase())
			}
		}).catch(() => {})
	}, [volcano.country])

	const elevationM = volcano.elevation
	const hasElevation = elevationM != null && elevationM !== 0

	return (
		<>
			{/* Title */}
			<div className="text-[22px] leading-none font-semibold text-white truncate">
				{ctx.title}
			</div>

			{/* Country + Region */}
			<div className="flex items-center gap-2 text-sm text-slate-300">
				{countryCode && (
					<img
						src={`${Constants.baseFlagUrl}/${countryCode}.svg`}
						alt={volcano.country}
						className="w-5 h-3.5 object-cover rounded-sm"
					/>
				)}
				<span>{volcano.country}</span>
				{volcano.region && (
					<span className="text-slate-500">&bull;</span>
				)}
				{volcano.region && (
					<span className="text-slate-400 truncate">{volcano.region}</span>
				)}
			</div>

			<MoreSection compact={ctx.compact}>
				{/* Volcano details grid */}
				<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
					<div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
						<InfoField label="Type" value={volcano.type} />
						{hasElevation && (
							<InfoField label="Elevation" value={formatAltitude(elevationM)} />
						)}
						{volcano.lastEruption && (
							<InfoField label="Last Eruption" value={volcano.lastEruption} />
						)}
						{volcano.epoch && (
							<InfoField label="Epoch" value={volcano.epoch} />
						)}
						{volcano.tectonicSetting && (
							<InfoField label="Tectonic" value={volcano.tectonicSetting} />
						)}
						{volcano.rockType && (
							<InfoField label="Rock Type" value={volcano.rockType} />
						)}
					</div>

					<CoordFooter
						lat={volcano.latitude}
						lon={volcano.longitude}
						className="pt-1 border-t border-white/5"
					/>
				</div>

				{/* Geological summary if available */}
				{volcano.geologicalSummary && (
					<div className="text-[11px] text-slate-400 leading-relaxed line-clamp-3">
						{volcano.geologicalSummary}
					</div>
				)}

				<CurrentConditionsSection
					lat={volcano.latitude}
					lon={volcano.longitude}
					accentClass="text-orange-400"
				/>
			</MoreSection>
		</>
	)
}
